import { WORLD_CONFIG } from '../../config/world.config';
import { INTERIOR, SEG_INTERIOR_40M } from '../../level/segments/definitions/SEG_INTERIOR_40M';
import { SEG_SHELL_60M, SHELL } from '../../level/segments/definitions/SEG_SHELL_60M';
import { SEG_TUNNEL_40M, TUNNEL } from '../../level/segments/definitions/SEG_TUNNEL_40M';
import type { CeilingZone, SegmentDefinition } from '../../level/segments/SegmentDefinition';
import { AssetCategory, GreyboxMaterialId as M, type AssetManifestEntry, type GreyboxMaterialId, type GreyboxPart } from '../AssetTypes';
import { box, pipeAlongZ } from './greybox';
import { pipeAlongX } from './terrainGreybox';

/**
 * Greyboxes of the enclosed modules (Phase 3) until their Blender modules exist. All
 * gameplay-relevant dimensions (roof, walls, low pipes, door headers, beams, columns, the
 * stair opening) come from the segment definitions, so greybox and collision never drift apart.
 *
 *   SEG_TUNNEL_40M     overlay on two SEG_STRAIGHT_20M road modules: concrete tube under an earth
 *                      berm, portals with hazard stripes, wall lamps, wall pipes, low pipes, puddles
 *   SEG_INTERIOR_40M   own concrete floor, drywall corridor, ceiling light strips, partition with
 *                      doors, hanging cables, building block around it
 *   SEG_SHELL_60M      own floor slab with stair opening, concrete frame (columns, two slabs),
 *                      orange guard rails, low beams, outside stairs, brick pallets
 */

const ceiling = (definition: SegmentDefinition, id: string): CeilingZone => {
  const zone = definition.terrain?.ceilings.find((c) => c.id === id);
  if (!zone) throw new Error(`${definition.id} has no ceiling ${id}`);
  return zone;
};

/** Box spanning a floor-plan rectangle between two heights (corner order does not matter). */
function block(x0: number, x1: number, y0: number, y1: number, z0: number, z1: number, material: GreyboxMaterialId): GreyboxPart {
  const [xFrom, xTo] = [Math.min(x0, x1), Math.max(x0, x1)];
  const [yFrom, yTo] = [Math.min(y0, y1), Math.max(y0, y1)];
  const [zFrom, zTo] = [Math.min(z0, z1), Math.max(z0, z1)];
  return box([xTo - xFrom, yTo - yFrom, zTo - zFrom], (xFrom + xTo) / 2, yFrom, (zFrom + zTo) / 2, material);
}

/** Pipe hanging across the track with its underside at the ceiling zone's clearance, hangers up to `roof`. */
function hangingPipe(zone: CeilingZone, roof: number): GreyboxPart[] {
  const diameter = zone.zTo - zone.zFrom;
  const z = (zone.zFrom + zone.zTo) / 2;
  const axisY = zone.clearance + diameter / 2;
  const span = zone.xTo - zone.xFrom;
  const parts: GreyboxPart[] = [pipeAlongX(diameter, span, (zone.xFrom + zone.xTo) / 2, axisY, z, M.Concrete)];
  const sleeves = Math.max(2, Math.round(span / 1.6));
  for (let i = 0; i < sleeves; i++) {
    const x = zone.xFrom + ((i + 0.5) * span) / sleeves;
    parts.push(pipeAlongX(diameter + 0.04, 0.35, x, axisY, z, M.Chevron));
  }
  for (const x of [zone.xFrom + 0.3, zone.xTo - 0.3]) {
    parts.push(block(x - 0.03, x + 0.03, axisY, roof, z - 0.03, z + 0.03, M.Steel));
  }
  return parts;
}

// ── SEG_14 tunnel ──────────────────────────────────────────────────────────

export function tunnelGreybox(): GreyboxPart[] {
  const T = TUNNEL;
  const L = T.length;
  const inner = T.wall.inner;
  const outer = inner + T.wall.thickness;
  const roofTop = T.roof.clearance + T.roof.thickness;
  const berm = { top: roofTop + 1.2, side: 6 };
  const parts: GreyboxPart[] = [
    // concrete tube
    block(-outer, -inner, 0, roofTop, 0, L, M.ConcreteDark),
    block(inner, outer, 0, roofTop, 0, L, M.ConcreteDark),
    block(-outer, outer, T.roof.clearance, roofTop, 0, L, M.ConcreteDark),
    // earth berm over and beside the tube
    block(-outer - berm.side, outer + berm.side, roofTop, berm.top, 0, L, M.TrackSide),
    block(-outer - berm.side, -outer, 0, roofTop, 0, L, M.TrackSide),
    block(outer, outer + berm.side, 0, roofTop, 0, L, M.TrackSide),
  ];
  // portals (inside the module): concrete header with a hazard stripe over the opening
  for (const [face, z] of [[-1, 0], [1, L]] as const) {
    const zFrom = face < 0 ? z : z - 0.3;
    const zTo = face < 0 ? z + 0.3 : z;
    parts.push(
      block(-outer - 0.3, outer + 0.3, T.roof.clearance, berm.top, zFrom, zTo, M.Concrete),
      block(-outer - 0.3, -inner, 0, T.roof.clearance, zFrom, zTo, M.Concrete),
      block(inner, outer + 0.3, 0, T.roof.clearance, zFrom, zTo, M.Concrete),
      block(-inner, inner, T.roof.clearance, T.roof.clearance + T.portalStripe, face < 0 ? zFrom - 0.02 : zTo, face < 0 ? zFrom : zTo + 0.02, M.Chevron),
    );
  }
  // wall lamps (glowing) in steel housings, pipes and a cable tray along the walls
  for (let z = T.lamps.spacing / 2; z < L; z += T.lamps.spacing) {
    for (const sign of [-1, 1] as const) {
      parts.push(
        block(sign * (inner - 0.04), sign * inner, T.lamps.height - 0.18, T.lamps.height + 0.18, z - 0.6, z + 0.6, M.Steel),
        block(sign * (inner - 0.1), sign * (inner - 0.04), T.lamps.height - 0.1, T.lamps.height + 0.1, z - 0.5, z + 0.5, M.Lamp),
      );
    }
  }
  for (const sign of [-1, 1] as const) {
    parts.push(
      pipeAlongZ(0.36, L, sign * (inner - 0.3), 3.8, L / 2, M.Steel),
      pipeAlongZ(0.2, L, sign * (inner - 0.2), 2.75, L / 2, M.Concrete),
      block(sign * (inner - 0.45), sign * (inner - 0.05), 4.0, 4.04, 0, L, M.Steel),
    );
  }
  parts.push(...hangingPipe(ceiling(SEG_TUNNEL_40M, 'CEIL_PIPE_A'), T.roof.clearance), ...hangingPipe(ceiling(SEG_TUNNEL_40M, 'CEIL_PIPE_B'), T.roof.clearance));
  // puddles (visual only): x, z, width, depth
  const puddles: readonly (readonly [number, number, number, number])[] = [
    [-2.0, 7.5, 1.8, 2.6],
    [0.6, 16, 2.4, 1.6],
    [2.3, 23.5, 1.5, 3.0],
    [-1.4, 31.5, 2.6, 1.8],
    [1.2, 38, 1.6, 1.4],
  ];
  for (const [x, z, w, d] of puddles) parts.push(box([w, 0.006, d], x, 0, z, M.Water));
  return parts;
}

// ── SEG_13 interior ────────────────────────────────────────────────────────

export function interiorGreybox(): GreyboxPart[] {
  const I = INTERIOR;
  const L = I.length;
  const inner = I.wall.inner;
  const outer = inner + I.wall.thickness;
  const top = I.ceiling.clearance;
  const half = WORLD_CONFIG.trackWidth / 2;
  const B = I.building;
  const parts: GreyboxPart[] = [
    block(-outer, outer, -0.2, 0, 0, L, M.Concrete),
    block(-outer, -inner, 0, top, 0, L, M.Drywall),
    block(inner, outer, 0, top, 0, L, M.Drywall),
    block(-outer, outer, top, top + I.ceiling.thickness, 0, L, M.Concrete),
    // building block around the corridor (other rooms, upper floor)
    block(-B.halfWidth, -outer, -0.2, B.height, 0, L, M.Concrete),
    block(outer, B.halfWidth, -0.2, B.height, 0, L, M.Concrete),
    block(-outer, outer, top + I.ceiling.thickness, B.height, 0, L, M.Concrete),
    // side ground around the building
    block(-30, -B.halfWidth, -0.22, -0.02, 0, L, M.TrackSide),
    block(B.halfWidth, 30, -0.22, -0.02, 0, L, M.TrackSide),
  ];
  // window band on the facade and a dark entrance frame
  for (const sign of [-1, 1] as const) {
    for (let z = 3; z < L - 2; z += 6) parts.push(block(sign * B.halfWidth - (sign > 0 ? 0 : 0.02), sign * B.halfWidth + (sign > 0 ? 0.02 : 0), 4.6, 6.4, z, z + 3.5, M.ContainerAlt));
  }
  // floor tape: lane dividers (dashed) and edge lines
  for (const x of [-1.1, 1.1]) for (let z = 1; z + 2 <= L; z += 4) parts.push(box([0.08, 0.004, 2], x, 0, z + 1, M.LaneLine));
  for (const x of [-half, half]) parts.push(box([0.08, 0.004, L], x, 0, L / 2, M.LaneLine));
  // ceiling light strips
  for (let z = I.lights.spacing / 2; z < L; z += I.lights.spacing) {
    for (const x of I.lights.rows) parts.push(box([0.25, 0.05, 1.4], x, top - 0.05, z, M.Lamp));
  }
  // partition with doors (dimensions from the ceiling zones)
  for (const id of ['CEIL_PARTITION_POST_1', 'CEIL_PARTITION_POST_2', 'CEIL_PARTITION_WALL_L', 'CEIL_PARTITION_WALL_R']) {
    const zone = ceiling(SEG_INTERIOR_40M, id);
    parts.push(block(zone.xFrom, zone.xTo, 0, top, zone.zFrom, zone.zTo, M.Drywall));
  }
  for (const id of ['CEIL_DOOR_L', 'CEIL_DOOR_R', 'CEIL_DOOR_LOW']) {
    const zone = ceiling(SEG_INTERIOR_40M, id);
    parts.push(block(zone.xFrom, zone.xTo, zone.clearance, top, zone.zFrom, zone.zTo, M.Drywall));
    // door frame (steel) and hazard stripe on the low header
    parts.push(block(zone.xFrom, zone.xTo, zone.clearance, zone.clearance + 0.06, zone.zFrom - 0.02, zone.zTo + 0.02, zone.id === 'CEIL_DOOR_LOW' ? M.Chevron : M.Steel));
  }
  // hanging cables: tray under the ceiling, strands and the sagging bundle (slide)
  const cables = ceiling(SEG_INTERIOR_40M, 'CEIL_CABLES');
  const cz = (cables.zFrom + cables.zTo) / 2;
  parts.push(
    block(cables.xFrom, cables.xTo, top - 0.35, top - 0.27, cables.zFrom, cables.zTo, M.Steel),
    block(cables.xFrom, cables.xTo, cables.clearance, cables.clearance + 0.15, cz - 0.12, cz + 0.12, M.Container),
  );
  for (let x = cables.xFrom + 0.4; x < cables.xTo; x += 0.9) parts.push(block(x - 0.03, x + 0.03, cables.clearance, top - 0.35, cz - 0.03, cz + 0.03, M.Container));
  // entrance and exit: hazard-striped lintels on the facade
  for (const z of [0, L]) parts.push(block(-outer, outer, top, top + 0.3, z - 0.02, z + 0.02, M.Chevron));
  return parts;
}

// ── SEG_12 shell building ──────────────────────────────────────────────────

export function shellGreybox(): GreyboxPart[] {
  const S = SHELL;
  const L = S.length;
  const W = S.slab.halfWidth;
  const t = S.slab.thickness;
  const gap = SEG_SHELL_60M.terrain?.gaps[0];
  const parts: GreyboxPart[] = [
    // second and roof slab, side ground
    block(-W, W, S.slab.clearance, S.slab.clearance + t, 0, L, M.Concrete),
    block(-W, W, S.slab.secondFloor, S.slab.secondFloor + t, 0, L, M.Concrete),
    block(-30, -W, -0.22, -0.02, 0, L, M.TrackSide),
    block(W, 30, -0.22, -0.02, 0, L, M.TrackSide),
  ];
  // ground floor slab around the stair opening
  if (gap) {
    parts.push(
      block(-W, W, -0.2, 0, 0, gap.zFrom, M.Concrete),
      block(-W, W, -0.2, 0, gap.zTo, L, M.Concrete),
      block(-W, gap.xFrom, -0.2, 0, gap.zFrom, gap.zTo, M.Concrete),
      block(gap.xTo, W, -0.2, 0, gap.zFrom, gap.zTo, M.Concrete),
      // opening: basement floor, shaft walls, hazard stripes on the rims, corner posts
      block(gap.xFrom, gap.xTo, -gap.depth - 0.1, -gap.depth, gap.zFrom, gap.zTo, M.ConcreteDark),
      block(gap.xFrom, gap.xFrom + 0.15, -gap.depth, 0, gap.zFrom, gap.zTo, M.ConcreteDark),
      block(gap.xTo - 0.15, gap.xTo, -gap.depth, 0, gap.zFrom, gap.zTo, M.ConcreteDark),
      block(gap.xFrom, gap.xTo, -gap.depth, 0, gap.zFrom, gap.zFrom + 0.15, M.ConcreteDark),
      block(gap.xFrom, gap.xTo, -gap.depth, 0, gap.zTo - 0.15, gap.zTo, M.ConcreteDark),
      block(gap.xFrom, gap.xTo, 0, 0.02, gap.zFrom - 0.25, gap.zFrom, M.Chevron),
      block(gap.xFrom, gap.xTo, 0, 0.02, gap.zTo, gap.zTo + 0.25, M.Chevron),
    );
  }
  // columns between the lanes (gameplay) and the outer frame every 8 m, both storeys
  for (const id of SEG_SHELL_60M.terrain?.ceilings.filter((c) => c.id.startsWith('CEIL_COLUMN_')).map((c) => c.id) ?? []) {
    const c = ceiling(SEG_SHELL_60M, id);
    parts.push(block(c.xFrom, c.xTo, 0, S.slab.secondFloor + t, c.zFrom, c.zTo, M.Concrete));
  }
  const h = S.column.size / 2;
  for (let z = 3; z < L; z += 8) {
    for (const x of [-S.column.outerX, S.column.outerX]) parts.push(block(x - h, x + h, 0, S.slab.secondFloor + t, z - h, z + h, M.Concrete));
  }
  // orange guard rails along both slab edges
  for (const y of [S.slab.clearance + t, S.slab.secondFloor + t]) {
    for (const sign of [-1, 1] as const) {
      parts.push(
        block(sign * W - 0.03, sign * W + 0.03, y + 0.95, y + 1.0, 0, L, M.Container),
        block(sign * W - 0.03, sign * W + 0.03, y + 0.45, y + 0.5, 0, L, M.Container),
      );
      for (let z = 0; z <= L; z += 2) parts.push(block(sign * W - 0.03, sign * W + 0.03, y, y + 1.0, z - 0.03, z + 0.03, M.Steel));
    }
  }
  // low site beams (slide), hung from the slab
  for (const id of ['CEIL_BEAM_A', 'CEIL_BEAM_B']) {
    const b = ceiling(SEG_SHELL_60M, id);
    parts.push(block(b.xFrom, b.xTo, b.clearance, b.clearance + 0.35, b.zFrom, b.zTo, M.Chevron));
    for (const x of [b.xFrom + 0.3, b.xTo - 0.3]) parts.push(block(x - 0.03, x + 0.03, b.clearance + 0.35, S.slab.clearance, b.zFrom + 0.1, b.zTo - 0.1, M.Steel));
  }
  // outside stairs to the first floor (right side, visual) and brick pallets
  const stair = { x0: 5.6, x1: 6.8, z0: 32, rise: 0.25, run: 0.5 };
  for (let i = 0; i * stair.rise < S.slab.clearance; i++) {
    parts.push(block(stair.x0, stair.x1, 0, (i + 1) * stair.rise, stair.z0 + i * stair.run, stair.z0 + (i + 1) * stair.run, M.Concrete));
  }
  for (const [x, z] of [[-6.2, 6], [-6.0, 19], [6.2, 22], [-6.3, 46], [6.1, 52]] as const) {
    parts.push(box([1.2, 0.14, 0.9], x, 0, z, M.Wood), box([1.1, 0.7, 0.85], x, 0.14, z, M.Container));
  }
  return parts;
}

/** Blender module (tools/blender/assets/<design>.py) published as track-modules/<id>.glb; the greybox stays as fallback. */
const enclosedAsset = (segment: SegmentDefinition, parts: readonly GreyboxPart[], design: string, description: string): AssetManifestEntry => ({
  id: segment.visualAssetId,
  category: AssetCategory.TrackModule,
  source: { kind: 'glb', url: `/assets/models/track-modules/${segment.visualAssetId.toLowerCase()}.glb`, fallback: parts },
  collision: { kind: 'none' },
  preload: 'run',
  instancing: 'instances',
  metadata: {
    description,
    status: 'wip',
    triangleBudget: 16000,
    materialBudget: 12,
    blenderSource: `design/05_environment/${design}/${design}.blend`,
  },
});

/** Manifest entries of the enclosed modules (spread into ASSET_MANIFEST). */
export const ENCLOSURE_MODULE_ASSETS: readonly AssetManifestEntry[] = [
  enclosedAsset(
    SEG_TUNNEL_40M,
    tunnelGreybox(),
    'ENV_Tunnel_A',
    'SEG_14 Keller/Tunnel (Overlay auf zwei SEG_01-Fahrbahnen): Betonröhre 4,2 m unter Erdwall, Portale, Wandleuchten, Rohre, niedrige Rohre 1,3 m (Rutschen), Pfützen',
  ),
  enclosedAsset(
    SEG_INTERIOR_40M,
    interiorGreybox(),
    'ENV_Interior_A',
    'SEG_13 Innenausbau: Trockenbau-Flur 3,8 m, Lichtbänder, Trennwand mit Türen (niedrige Tür 1,3 m), abgehängte Kabel 1,3 m, Gebäudehülle',
  ),
  enclosedAsset(
    SEG_SHELL_60M,
    shellGreybox(),
    'ENV_Shell_A',
    'SEG_12 Rohbau: Betonskelett mit Decken 4,0 / 7,6 m, Stützen zwischen den Spuren, Baubalken 1,3 m (Rutschen), Treppenloch, Geländer, Außentreppe',
  ),
];
